import React, {Component} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import TextField from '@material-ui/core/TextField';
import { lighten } from '@material-ui/core/styles/colorManipulator';

import moment from "moment";
import CardHeader from "../../modules/Card/CardHeader.jsx";

const styles = theme => ({
    root: {
        width: '100%',
        marginTop: theme.spacing.unit * 3,
    },
    table: {
        minWidth: 1020,
    },
    tableWrapper: {
        overflowX: 'auto',
    }
    , button: {
        margin: theme.spacing.unit,
    },
});

function desc(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const rows = [
    { id: 'id', numeric: false, disablePadding: false, label: 'Deal ID' },
    { id: 'title', numeric: false, disablePadding: false, label: 'Deal Title' },
    { id: 'org_name', numeric: false, disablePadding: false, label: 'Client' },
    { id: 'owner_name', numeric: false, disablePadding: false, label: 'Sales' },
    { id: 'add_time', numeric: false, disablePadding: false, label: 'Add Time' }
];

class EnhancedTableHead extends Component {
    createSortHandler = property => event => {
        this.props.onRequestSort(event, property);
    };

    render() {
        const { onSelectAllClick, order, orderBy, numSelected, rowCount } = this.props;

        return (
            <TableHead>
                <TableRow>
                    <TableCell padding="checkbox">
                        <Checkbox
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={numSelected === rowCount}
                            onChange={onSelectAllClick}
                        />
                    </TableCell>
                    {rows.map(row => {
                        return (
                            <TableCell
                                key={row.id}
                                numeric={row.numeric}
                                padding={row.disablePadding ? 'none' : 'default'}
                                sortDirection={orderBy === row.id ? order : false}
                            >
                                <Tooltip
                                    title="Sort"
                                    placement={row.numeric ? 'bottom-end' : 'bottom-start'}
                                    enterDelay={300}
                                >
                                    <TableSortLabel
                                        active={orderBy === row.id}
                                        direction={order}
                                        onClick={this.createSortHandler(row.id)}
                                    >
                                        {row.label}
                                    </TableSortLabel>
                                </Tooltip>
                            </TableCell>
                        );
                    }, this)}
                </TableRow>
            </TableHead>
        );
    }
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const toolbarStyles = theme => ({
    root: {
        paddingRight: theme.spacing.unit,
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                color: theme.palette.secondary.main,
                backgroundColor: lighten(theme.palette.secondary.light, 0.85),
            }
            : {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.secondary.dark,
            },
    spacer: {
        flex: '1 1 100%',
    },
    actions: {
        color: theme.palette.text.secondary,
    },
    title: {
        flex: '0 0 auto',
    },
});

let EnhancedTableToolbar = props => {
    const { numSelected, classes, handleAddClick, handleSearchChange } = props;

    return (
        <Toolbar
            className={classNames(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            <div className={classes.title}>
                {numSelected > 0 ? (
                    <Typography color="inherit" variant="subtitle1">
                        {numSelected} selected
                    </Typography>
                ) : (
                        <CardHeader color="success">
                            <div style={{ height: '5vh' }}>Pipedrive</div>
                        </CardHeader>
                    )}
            </div>
            <div className={classes.spacer} />
            <div className={classes.actions}>
                {numSelected > 0 ? (
                    <Tooltip title="Add Deal to Project">
                        <IconButton aria-label="Add">
                            <AddIcon onClick={handleAddClick} />
                        </IconButton>
                    </Tooltip>
                ) : null}
            </div>

            <TextField
                label="Search"
                style={{ width: '80vh' }}
                margin="normal"
                placeholder="Search"
                variant="outlined"
                onChange={handleSearchChange()}
            />
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
};

EnhancedTableToolbar = withStyles(toolbarStyles)(EnhancedTableToolbar);

class EnhancedTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            order: 'desc',
            orderBy: 'id',
            selected: [],
            data: convert(props.data),
            page: 0,
            rowsPerPage: 5,
            searchStr: ''
        };
        this.handleAddClick = this.handleAddClick.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.data) {
            this.setState({
                data: convert(nextProps.data)
            })
        }
    }

    handleRequestSort = (event, property) => {
        const orderBy = property;
        let order = 'desc';

        if (this.state.orderBy === property && this.state.order === 'desc') {
            order = 'asc';
        }

        this.setState({ order, orderBy });
    };

    handleSelectAllClick = event => {
        if (event.target.checked) {
            this.setState(state => ({ selected: state.data.map(n => n.id) }));
            return;
        }
        this.setState({ selected: [] });
    };

    handleAddClick() {
        this.props.handleAddClick(this.state.selected)
    }

    handleClick = (event, n) => {
        const { selected } = this.state;
        const selectedIndex = selected.indexOf(n.id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, n.id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        if (event === 'check') {
            this.setState({ selected: newSelected });
        }
    };

    handleChangePage = (event, page) => {
        this.setState({ page });
    };

    handleChangeRowsPerPage = event => {
        this.setState({ rowsPerPage: event.target.value });
    };

    isSelected = id => this.state.selected.indexOf(id) !== -1;

    handleSearchChange = name => event => {
        this.setState({
            searchStr: event.target.value
        })
    };

    render() {
        const { classes } = this.props;
        const { order, orderBy, selected, rowsPerPage, page } = this.state;
        const data = findSearchStr(this.state.data, this.state.searchStr);
        const emptyRows = rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);
        const formatter = 'YYYY-MM-DD HH:mm:ss';

        return (
            <Paper className={classes.root}>
                <EnhancedTableToolbar 
                    numSelected={selected.length} 
                    handleAddClick={this.handleAddClick}
                    handleSearchChange={this.handleSearchChange}
                    />
                <div className={classes.tableWrapper}>
                    <Table id="PipedriveTable" className={classes.table} aria-labelledby="tableTitle">
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={this.handleSelectAllClick}
                            onRequestSort={this.handleRequestSort}
                            rowCount={data.length}
                        />
                        <TableBody>
                            {stableSort(data, getSorting(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map(n => {
                                    const isSelected = this.isSelected(n.id);
                                    return (
                                        <TableRow
                                            hover
                                            role="checkbox"
                                            aria-checked={isSelected}
                                            tabIndex={-1}
                                            key={n.id}
                                            selected={isSelected}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isSelected} onClick={event => this.handleClick('check', n)} />
                                                {/* 
                                                <IconButton color="secondary" aria-label="Edit" className={styles.button} onClick={event => this.handleClick('edit', n)}>
                                                    <EditIcon />
                                                </IconButton> 
                                                */}
                                            </TableCell>
                                            <TableCell component="th" scope="row">{n.id}</TableCell>
                                            <TableCell>{n.title}</TableCell>
                                            <TableCell>{n.org_name}</TableCell>
                                            <TableCell>{n.owner_name}</TableCell>
                                            <TableCell>{moment.utc(n.add_time).local().format(formatter)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 49 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <TablePagination
                    component="div"
                    count={data.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    backIconButtonProps={{
                        'aria-label': 'Previous Page',
                    }}
                    nextIconButtonProps={{
                        'aria-label': 'Next Page',
                    }}
                    onChangePage={this.handleChangePage}
                    onChangeRowsPerPage={this.handleChangeRowsPerPage}
                />
            </Paper>
        );
    }
}

function findSearchStr(data, searchStr) {
    var filterEmpty = data.filter(function (item) {
        if (searchStr === 0 || searchStr === '') {
            return true
        }
        else {
            const idColumn = item.id ? item.id.toString() : '';
            const orgameColumn = item.org_name ? item.org_name : '';
            const ownername_Column = item.owner_name ? item.owner_name : '';
            const titleColumn = item.title ? item.title : '';

            for (let i = 0; i < idColumn.length; i++) {
                if (idColumn.toUpperCase().substring(i, i + searchStr.length) === searchStr) {
                    return true
                }
            }

            for (let i = 0; i < orgameColumn.length; i++) {
                if (orgameColumn.toUpperCase().substring(i, i + searchStr.length) === searchStr.toUpperCase()) {
                    return true
                }
            }

            for (let i = 0; i < ownername_Column.length; i++) {
                if (ownername_Column.toUpperCase().substring(i, i + searchStr.length) === searchStr.toUpperCase()) {
                    return true
                }
            }

            for (let i = 0; i < titleColumn.length; i++) {
                if (titleColumn.toUpperCase().substring(i, i + searchStr.length) === searchStr.toUpperCase()) {
                    return true
                }
            }

            return false
        }
    })

    return filterEmpty
}

function convert(data) {
    let convertData = data.map((value, key) => {
        return {
            id: value.id,
            title : value.title,
            org_name : value.org_name,
            owner_name : value.owner_name,
            add_time : value.add_time,
        };
    });

    return convertData;
}

EnhancedTable.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(EnhancedTable);


// WEBPACK FOOTER //
// ./src/components/Projects/EnhancedTablePipedrive.js